<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';

defineProps({
  canUpload: { type: Boolean, default: false },
  pending: { type: Array, default: () => [] },
});

const emit = defineEmits(['files']);
const { t } = useI18n();
const input = ref(null);
const dragging = ref(false);
const choose = () => input.value?.click();
const files = event => {
  emit('files', event.target.files);
  event.target.value = '';
};
const drop = event => {
  dragging.value = false;
  emit('files', event.dataTransfer.files);
};
</script>

<template>
  <div class="grid gap-4">
    <div
      v-if="canUpload"
      data-test="sales-party-documents-dropzone"
      class="mb-[30px] flex w-full cursor-pointer flex-col items-center justify-center rounded-[10px] border-2 border-dashed border-[#2DD2C5] bg-[#EDF8F7] p-[35px] text-center transition-colors"
      :class="
        dragging
          ? 'border-[#01988C] bg-[#EDF8F7]'
          : 'border-[#2DD2C5] bg-[#EDF8F7]'
      "
      @dragenter.prevent="dragging = true"
      @dragover.prevent="dragging = true"
      @dragleave.prevent="dragging = false"
      @drop.prevent="drop"
      @click="choose"
    >
      <div class="flex flex-col items-center">
        <span
          class="i-lucide-file-plus mb-[10px] size-10 text-[#01988C]"
          aria-hidden="true"
        />
        <div class="grid justify-items-center">
          <p
            class="mb-7 text-xl font-normal leading-6 text-[#464F53] max-[750px]:hidden"
            data-test="sales-party-documents-drop-text"
          >
            {{ t('SALES_PARTIES.DOCUMENTS.DROP') }}
          </p>
          <p
            class="mb-7 text-xl font-normal leading-6 text-[#464F53] max-[750px]:hidden"
            data-test="sales-party-documents-or-text"
          >
            {{ t('SALES_PARTIES.DOCUMENTS.OR') }}
          </p>
        </div>
        <input
          ref="input"
          class="hidden"
          type="file"
          accept=".doc,.docx,.pdf,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          multiple
          @change="files"
        />
        <LaisPrimaryButton
          class="!min-h-12 !w-[182px] !whitespace-nowrap !rounded-[10px] !bg-[#14B8AA] !px-[15px] !py-[10px] !text-[14px] !font-bold hover:!bg-[#01655D]"
          @click.stop="choose"
        >
          {{ t('SALES_PARTIES.DOCUMENTS.CHOOSE') }}
        </LaisPrimaryButton>
      </div>
    </div>

    <div v-if="pending.length" class="grid gap-2">
      <div
        v-for="item in pending"
        :key="item.key"
        class="rounded-lais-sm border border-lais-border-soft bg-white p-3"
      >
        <div class="flex items-center justify-between gap-3 text-sm">
          <span class="truncate font-medium text-lais-text">{{
            item.name
          }}</span>
          <span class="text-lais-text-muted">{{
            t('SALES_PARTIES.DOCUMENTS.PROGRESS', { value: item.progress })
          }}</span>
        </div>
        <progress
          class="mt-2 h-1.5 w-full accent-lais-primary"
          :value="item.progress"
          max="100"
        />
        <p v-if="item.error" class="mt-2 text-xs text-lais-danger">
          {{ item.error }}
        </p>
      </div>
    </div>
  </div>
</template>
